import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { C } from "../lib/theme";

const UPDATED = "August 2026";
const CONTACT = "andrew@kassandmoses.com";

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl text-ink">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen overflow-y-auto bg-bg text-ink" data-testid="privacy-page">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Link to="/" className="press mono-label inline-flex items-center gap-1.5 text-muted hover:text-ink">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Fathom
        </Link>

        <h1 className="mt-8 font-display text-3xl">Privacy Policy</h1>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
          Last updated {UPDATED}
        </p>

        <p className="mt-8 text-sm leading-relaxed text-muted">
          Fathom is a learning app. It teaches a topic, then checks whether you can explain it.
          This page describes exactly what it stores and who it sends data to. It is short because
          the app collects little.
        </p>

        <Section title="Using Fathom without an account">
          <p>
            You can use Fathom without signing in. When you do, your browser generates a random
            identifier and stores it locally. It contains no personal information and is not tied
            to you as a person &mdash; it exists only so your topics reappear when you return.
          </p>
          <p>
            Your read-aloud voice and speed preferences are also stored in your browser. Clearing
            your browser data removes all of this, and the topics linked to that identifier become
            unreachable.
          </p>
        </Section>

        <Section title="If you sign in with Google">
          <p>
            Signing in is optional. If you choose it, Google gives Fathom three things: your email
            address, your name, and your profile picture. Fathom requests nothing else &mdash; it
            cannot read your Gmail, Drive, Calendar, contacts, or any other Google data.
          </p>
          <p>
            Your email identifies your account so your topics follow you across devices. Any topics
            you began anonymously on that browser are merged into your account when you first sign in.
          </p>
        </Section>

        <Section title="What the app stores">
          <p>Alongside your account record, Fathom saves the work you do:</p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li>Topics you study</li>
            <li>Your quiz answers and confidence ratings</li>
            <li>Explanations you type when teaching the concept back</li>
            <li>Notes you write</li>
            <li>Scores, mastered count, and daily streak</li>
          </ul>
          <p>
            This is stored in a MongoDB Atlas database and exists so the app can track your progress
            and adapt lessons to the gaps it finds.
          </p>
        </Section>

        <Section title="Services that receive your data">
          <p>Fathom relies on a few outside services to work:</p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li>
              <span className="text-ink">Anthropic</span> &mdash; the topic you enter, the explanations
              you type, and your notes are sent to Claude to generate lessons, grade written answers,
              and produce your study card.
            </li>
            <li>
              <span className="text-ink">OpenAI</span> &mdash; when you use read-aloud, the lesson text
              being spoken is sent to generate the audio. Your personal information is not.
            </li>
            <li>
              <span className="text-ink">Google</span> &mdash; only if you choose to sign in.
            </li>
            <li>
              <span className="text-ink">MongoDB Atlas</span> and <span className="text-ink">Render</span>
              {" "}&mdash; database storage and application hosting.
            </li>
          </ul>
          <p>
            Each of these handles data under its own privacy policy. Fathom does not sell your data,
            does not show advertising, and does not use third-party analytics or tracking.
          </p>
        </Section>

        <Section title="Deleting your data">
          <p>
            You can delete any topic from the sidebar at any time, which removes it and its contents
            permanently.
          </p>
          <p>
            To delete your whole account and everything attached to it, email{" "}
            <a href={`mailto:${CONTACT}`} className="underline" style={{ color: C.blu }}>
              {CONTACT}
            </a>{" "}
            from the address you signed in with, and it will be removed.
          </p>
        </Section>

        <Section title="Security">
          <p>
            Sign-in sessions use a cookie that JavaScript cannot read, sent only over an encrypted
            connection, and expiring after seven days. Fathom never receives or stores your Google
            password.
          </p>
          <p>
            No system is perfectly secure, so please treat what you type into any web app with
            reasonable judgement.
          </p>
        </Section>

        <Section title="Children">
          <p>
            Fathom is not directed at children under 13 and does not knowingly collect their
            information.
          </p>
        </Section>

        <Section title="Changes and contact">
          <p>
            If this policy changes materially, the date at the top will change with it. Questions
            about privacy or your data go to{" "}
            <a href={`mailto:${CONTACT}`} className="underline" style={{ color: C.blu }}>
              {CONTACT}
            </a>
            .
          </p>
        </Section>

        <div className="mt-12 border-t border-hair pt-6">
          <Link to="/" className="press mono-label text-muted hover:text-ink">
            &larr; Back to Fathom
          </Link>
        </div>
      </div>
    </div>
  );
}
